import React, { useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Divider,
  Button,
  Stack,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Download as DownloadIcon,
  Print as PrintIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const PayslipPDF = ({ data }) => {
  const theme = useTheme();
  const payslipRef = useRef(null);

  const formatCurrency = (amount) => {
    if (!amount) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(parseFloat(amount));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "long",
        year: "numeric",
      });
    } catch (e) {
      return "-";
    }
  };

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const handleDownloadPDF = async () => {
    const element = payslipRef.current;
    if (!element) return;

    try {
      // Pre-load the logo image
      if (data.logo_url) {
        try {
          const response = await fetch(data.logo_url);
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          // Replace the image src with the blob URL
          const img = element.querySelector('img[alt="Company Logo"]');
          if (img) {
            img.src = url;
          }
        } catch (e) {
          console.warn("Could not pre-load logo:", e);
        }
      }

      const canvas = await html2canvas(element, {
        scale: 3,
        useCORS: true,
        allowTaint: true,
        logging: false,
        backgroundColor: "#ffffff",
        onclone: (clonedDoc) => {
          const clonedImages = clonedDoc.querySelectorAll("img");
          clonedImages.forEach((img) => {
            img.crossOrigin = "anonymous";
            img.referrerPolicy = "no-referrer";
          });
        },
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      // Add the first page
      pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight);
      heightLeft -= pageHeight;

      // Add extra pages if needed
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(
        `Payslip_${data.employee_code}_${data.payroll_month}_${data.payroll_year}.pdf`,
      );
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Custom Table Component with Full Width
  const CustomTable = ({ title, rows, isDeduction = false }) => (
    <TableContainer
      sx={{
        border: "1px solid #d1d5db",
        borderRadius: "4px",
        overflow: "hidden",
        height: "100%",
      }}
    >
      <Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
        <TableHead>
          <TableRow
            sx={{ backgroundColor: isDeduction ? "#fef2f2" : "#f0fdf4" }}
          >
            <TableCell
              sx={{
                fontWeight: 700,
                fontSize: "0.7rem",
                color: isDeduction ? "#dc2626" : "#059669",
                borderBottom: "2px solid #d1d5db",
                py: 0.8,
                width: "65%",
                borderRight: "1px solid #d1d5db",
              }}
            >
              {title}
            </TableCell>
            <TableCell
              sx={{
                fontWeight: 700,
                fontSize: "0.7rem",
                color: isDeduction ? "#dc2626" : "#059669",
                borderBottom: "2px solid #d1d5db",
                py: 0.8,
                textAlign: "right",
                width: "35%",
              }}
            >
              Amount (₹)
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow
              key={index}
              sx={{
                "&:last-child td": {
                  borderBottom: "none",
                },
              }}
            >
              <TableCell
                sx={{
                  fontSize: "0.7rem",
                  color: "#374151",
                  borderBottom: "1px solid #d1d5db",
                  borderRight: "1px solid #d1d5db",
                  py: 0.5,
                }}
              >
                {row.label}
              </TableCell>
              <TableCell
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: row.isBold ? 700 : 400,
                  color: row.isBold ? "#000" : "#374151",
                  borderBottom: "1px solid #d1d5db",
                  py: 0.5,
                  textAlign: "right",
                  fontFamily: "monospace",
                }}
              >
                {row.value !== undefined &&
                  row.value !== null &&
                  row.value !== "" &&
                  row.value !== "-1.00"
                  ? formatCurrency(row.value)
                  : "-"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box
      sx={{
        p: 2,

        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        "@media print": { p: 0, backgroundColor: "transparent" },
      }}
    >
      {/* NEW INNER WRAPPER CONTAINER */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "210mm",
          mx: "auto",
          "@media print": { maxWidth: "100%" },
        }}
      >
        {/* Payslip Content Card */}
        <Paper
          ref={payslipRef}
          sx={{
            p: 4,
            width: "100%",
            backgroundColor: "#ffffff",
            position: "relative",
            boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.05)",
            borderRadius: "8px",
            "@media print": {
              boxShadow: "none",
              borderRadius: 0,
              p: 3,
            },
          }}
        >
          {/* Top Border */}
          <Box
            sx={{
              height: 4,
              background:
                "linear-gradient(90deg, #f59e0b 0%, #d97706 50%, #f59e0b 100%)",
              mb: 3,
            }}
          />

          {/* Header - Company Logo & Title */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 4,
              pb: 2.5,
              borderBottom: "1px solid #e5e7eb",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 3 }}>
              {data.logo_url && (
                <Box
                  sx={{
                    height: 50,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                  }}
                >
                  <img
                    src={data.logo_url}
                    alt="Company Logo"
                    style={{
                      height: "100%",
                      width: "auto",
                      objectFit: "contain",
                    }}
                    crossOrigin="anonymous"
                    referrerPolicy="no-referrer"
                  />
                </Box>
              )}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "#111827",
                    letterSpacing: "0.5px",
                    lineHeight: 1.2,
                    mb: 0.5,
                  }}
                >
                  {data.company_name || "EBS My Company"}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.7rem",
                    color: "#6b7280",
                    fontWeight: 500,
                  }}
                >
                  {data.department_name}{" "}
                  {data.department_code ? `(${data.department_code})` : ""}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ textAlign: "right" }}>
              <Typography
                sx={{
                  fontSize: "0.95rem",
                  fontWeight: 800,
                  color: "#d97706",
                  letterSpacing: "1.5px",
                  lineHeight: 1.2,
                  mb: 0.5,
                }}
              >
                PAYSLIP
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  color: "#374151",
                  fontFamily: "monospace",
                }}
              >
                #{String(data.payslip_id || data.id).padStart(6, "0")}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.65rem",
                  fontWeight: 500,
                  color: "#6b7280",
                  mt: 0.2,
                }}
              >
                {months[data.payroll_month - 1]} {data.payroll_year}
              </Typography>
            </Box>
          </Box>


          {/* Employee & Transaction Details */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Employee Information */}
            <Grid item xs={6}>
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  color: "#374151",
                  borderBottom: "2px solid #f59e0b",
                  pb: 0.6,
                  mb: 1.5,
                  display: "inline-block",
                  minWidth: 180,
                }}
              >
                Employee Information
              </Typography>

              <Typography
                sx={{
                  fontSize: "1rem",
                  fontWeight: 700,
                  mb: 1.5,
                  color: "#111827",
                }}
              >
                {data.employee_name}
              </Typography>

              <Stack spacing={0.7}>
                {[
                  ["Code / ID", `${data.employee_code} / #${data.employee_id}`],
                  ["Department", data.department_name || "—"],
                  ["PAN Number", data.pan_number || "—"],
                  ["UAN Number", data.uan_number || "—"],
                ].map(([label, value]) => (
                  <Box
                    key={label}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "150px 15px 1fr",
                      alignItems: "center",
                      minHeight: 24,
                    }}
                  >
                    <Typography sx={{ fontSize: "0.72rem", color: "#6b7280" }}>
                      {label}
                    </Typography>

                    <Typography sx={{ textAlign: "center", color: "#9ca3af" }}>
                      :
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "0.74rem",
                        fontWeight: 600,
                        color: "#374151",
                        fontFamily:
                          label === "PAN Number" || label === "UAN Number"
                            ? "monospace"
                            : "inherit",
                      }}
                    >
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Grid>

            {/* Transaction Details */}
            <Grid item xs={6}>
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  color: "#374151",
                  borderBottom: "2px solid #10b981",
                  pb: 0.6,
                  mb: 1.5,
                  display: "inline-block",
                  minWidth: 180,
                }}
              >
                Transaction Details
              </Typography>

              <Stack spacing={0.7}>
                {[
                  ["Payment Date", formatDate(data.payment_date)],
                  [
                    "Payment Method",
                    data.payment_method?.replace("_", " ") || "—",
                  ],
                  ["Bank Name", data.bank_name || "—"],
                  ["Account Number", data.bank_account_number || "—"],
                  ["IFSC Code", data.ifsc_code || "—"],
                ].map(([label, value]) => (
                  <Box
                    key={label}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "150px 15px 1fr",
                      alignItems: "center",
                      minHeight: 24,
                    }}
                  >
                    <Typography sx={{ fontSize: "0.72rem", color: "#6b7280" }}>
                      {label}
                    </Typography>

                    <Typography sx={{ textAlign: "center", color: "#9ca3af" }}>
                      :
                    </Typography>

                    <Typography
                      sx={{
                        fontSize: "0.74rem",
                        fontWeight: 600,
                        color: "#374151",
                        fontFamily:
                          label === "Account Number" || label === "IFSC Code"
                            ? "monospace"
                            : "inherit",
                      }}
                    >
                      {value}
                    </Typography>
                  </Box>
                ))}

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "150px 15px auto",
                    alignItems: "center",
                    minHeight: 24,
                    mt: 0.5,
                  }}
                >
                  <Typography sx={{ fontSize: "0.72rem", color: "#6b7280" }}>
                    Payment Status
                  </Typography>

                  <Typography sx={{ textAlign: "center", color: "#9ca3af" }}>
                    :
                  </Typography>

                  <Box
                    sx={{
                      display: "inline-flex",
                      px: 1,
                      py: 0.25,
                      borderRadius: "4px",
                      backgroundColor:
                        data.payment_status === "success"
                          ? "#dcfce7"
                          : "#fee2e2",
                      color:
                        data.payment_status === "success"
                          ? "#166534"
                          : "#b91c1c",
                      fontSize: "0.68rem",
                      fontWeight: 700,
                      width: "fit-content",
                      textTransform: "uppercase",
                    }}
                  >
                    {data.payment_status || "—"}
                  </Box>
                </Box>
              </Stack>
            </Grid>
          </Grid>

          {/* Row 1: EARNINGS & ALLOWANCES - Full Width */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <CustomTable
                title="EARNINGS"
                rows={[
                  { label: "Basic Salary", value: data.basic },
                  {
                    label: "House Rent Allowance (HRA)",
                    value: data.hr_allowance,
                  },
                  { label: "Dearness Allowance (DA)", value: data.da },
                  { label: "Special Allowance", value: data.special_allowance },
                ]}
              />
            </Grid>
            <Grid item xs={6}>
              <CustomTable
                title="ALLOWANCES"
                rows={[
                  {
                    label: "Conveyance Allowance",
                    value: data.conveyance_allowance,
                  },
                  { label: "Mobile Allowance", value: data.mobile_allowance },
                  { label: "Travel Allowance", value: data.travel_allowance },
                  { label: "Other Allowance", value: data.other_allowance },
                ]}
              />
            </Grid>
          </Grid>

          {/* Row 2: BENEFITS & EXTRAS + DEDUCTIONS - Full Width */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6}>
              <CustomTable
                title="BENEFITS & EXTRAS"
                rows={[
                  { label: "Incentive", value: data.incentive },
                  { label: "Bonus", value: data.bonus },
                  { label: "Mediclaim", value: data.mediclaim },
                  {
                    label: "Attendance Based Salary",
                    value: data.attendance_based_salary || data.gross_salary,
                  },
                ]}
              />
            </Grid>
            <Grid item xs={6}>
              <CustomTable
                title="DEDUCTIONS"
                isDeduction={true}
                rows={[
                  {
                    label: "Provident Fund (PF)",
                    value:
                      data.pf_applicable === 1
                        ? data.pf_type === "number"
                          ? parseFloat(data.pf_amount) || 0
                          : (parseFloat(data.basic) * (parseFloat(data.pf_percentage) !== undefined && parseFloat(data.pf_percentage) !== null && !isNaN(parseFloat(data.pf_percentage)) ? parseFloat(data.pf_percentage) : 12)) / 100
                        : 0,
                  },
                  {
                    label: "Employees' State Insurance (ESI)",
                    value:
                      data.esi_applicable === 1
                        ? data.esi_type === "number"
                          ? parseFloat(data.esi_amount) || 0
                          : (parseFloat(data.gross_salary) * (parseFloat(data.esi_percentage) !== undefined && parseFloat(data.esi_percentage) !== null && !isNaN(parseFloat(data.esi_percentage)) ? parseFloat(data.esi_percentage) : 0.75)) / 100
                        : 0,
                  },
                  {
                    label: "Professional Tax (PT)",
                    value:
                      data.pt_applicable === 1
                        ? data.pt_type === "number"
                          ? parseFloat(data.pt_amount) !== undefined && parseFloat(data.pt_amount) !== null && !isNaN(parseFloat(data.pt_amount)) ? parseFloat(data.pt_amount) : 200
                          : (parseFloat(data.gross_salary) * (parseFloat(data.pt_percentage) || 0)) / 100
                        : 0,
                  },
                  { label: "Tax Deducted at Source (TDS)", value: data.tds },
                ]}
              />
            </Grid>
          </Grid>

          {/* Additional Info & Payroll Summary - Side by Side */}
          <Grid container spacing={36} sx={{ mb: 3 }}>
            {/* Left Column: Additional Information (Minimalist Clean Row Style) */}
            <Grid item xs={6}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: "#475569",
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    pb: 1,
                    mb: 1.5,
                    borderBottom: "1px solid #e2e8f0",
                  }}
                >
                  Additional Information
                </Typography>

                <Stack
                  spacing={1.2}
                  sx={{ flexGrow: 1, justifyContent: "center" }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color: "#6b7280",
                        maxWidth: "60%",
                      }}
                    >
                      Attendance (Days / Payable)
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: "#111827",
                      }}
                    >
                      {data.attendance_days} / {data.payable_days}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography sx={{ fontSize: "0.7rem", color: "#6b7280" }}>
                      Payment Method
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        color: "#111827",
                      }}
                    >
                      {data.payment_method?.replace("_", " ") || "-"}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography sx={{ fontSize: "0.7rem", color: "#6b7280" }}>
                      Disbursement ID
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        fontFamily: "monospace",
                        color: "#111827",
                      }}
                    >
                      #{data.disbursement_id}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography sx={{ fontSize: "0.7rem", color: "#6b7280" }}>
                      Generated At
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: "#475569" }}>
                      {formatDate(data.generated_at)}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Grid>

            {/* Right Column: Payroll Summary (Asymmetric Modern Layout) */}
            <Grid item xs={6}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    color: "#b45309",
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    pb: 1,
                    mb: 1.5,
                    borderBottom: "1px solid #fef3c7",
                  }}
                >
                  Payroll Summary
                </Typography>

                <Stack spacing={1.2}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography sx={{ fontSize: "0.7rem", color: "#475569" }}>
                      Gross Salary
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        fontFamily: "monospace",
                        color: "#111827",
                      }}
                    >
                      {formatCurrency(data.gross_salary)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography sx={{ fontSize: "0.7rem", color: "#475569" }}>
                      CTC Component
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        fontFamily: "monospace",
                        color: "#111827",
                      }}
                    >
                      {formatCurrency(data.total_ctc_component)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography sx={{ fontSize: "0.7rem", color: "#475569" }}>
                      Total Deduction
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        fontFamily: "monospace",
                        color: "#dc2626",
                      }}
                    >
                      {formatCurrency(data.total_deduction)}
                    </Typography>
                  </Box>

                  {/* Highlighted Net Salary Block */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mt: 0.5,
                      p: 1.2,
                      borderRadius: "4px",
                      backgroundColor: "#f0fdf4",
                      borderLeft: "4px solid #10b981",
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          color: "#065f46",
                          maxWidth: "60%",
                        }}
                      >
                        Net Salary
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 800,
                        fontFamily: "monospace",
                        color: "#059669",
                      }}
                    >
                      {formatCurrency(data.net_salary)}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Grid>
          </Grid>

          {/* Footer */}
          <Box
            sx={{
              pt: 2,
              borderTop: "2px solid #e5e7eb",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: "0.5rem", color: "#9ca3af" }}>
              This is a computer-generated payslip. No signature required.
            </Typography>
            <Typography
              sx={{
                fontSize: "0.5rem",
                color: "#9ca3af",
                fontFamily: "monospace",
              }}
            >
              {data.company_name} • Payslip #
              {String(data.payslip_id || data.id).padStart(6, "0")}
            </Typography>
          </Box>

          {/* Bottom Border */}
          <Box
            sx={{
              height: 3,
              background:
                "linear-gradient(90deg, #f59e0b 0%, #d97706 50%, #f59e0b 100%)",
              mt: 2,
            }}
          />
        </Paper>

        {/* Action Buttons */}
        <Stack
          direction="row"
          spacing={2}
          justifyContent="center"
          sx={{ mt: 3, "@media print": { display: "none" } }}
        >
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadPDF}
            sx={{
              borderRadius: "8px",
              px: 4,
              py: 0.8,
              textTransform: "none",
              fontWeight: 600,
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
              },
            }}
          >
            Download PDF
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default PayslipPDF;
